import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatFrDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function countdownLabel(targetIso) {
  try {
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "imminente";
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `dans ${d} j ${h} h`;
    if (h > 0) return `dans ${h} h ${m} min`;
    return `dans ${m} min`;
  } catch { return ""; }
}

/**
 * Banniere informative affichee en haut du site lorsqu'une maintenance
 * est programmee. Disparait apres la fin de la fenetre. L'utilisateur
 * peut la masquer pour la session (localStorage).
 */
export default function ScheduledMaintenanceBanner() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await axios.get(`${API}/system/scheduled-maintenance`);
        if (!cancelled) setData(d?.active ? d : null);
      } catch {
        if (!cancelled) setData(null);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!data) return;
    const key = `bypassgo:sched-maint-hide:${data.startsAt}`;
    if (sessionStorage.getItem(key) === "1") setHidden(true);
  }, [data]);

  if (!data || hidden) return null;

  // Si on est apres endsAt, on ne montre plus rien (l'API filtrera aussi).
  const now = Date.now();
  const ends = new Date(data.endsAt).getTime();
  if (now >= ends) return null;

  const starts = new Date(data.startsAt).getTime();
  const isLive = now >= starts && now < ends;
  const isImminent = !isLive && (starts - now) < 60 * 60 * 1000; // < 1 h
  // Couleur : bleu informatif tant que c'est loin, ambre si < 1 h, rouge si en cours.
  const tone = isLive
    ? { bg: "bg-rose-50", border: "border-rose-400", icon: "text-rose-700", text: "text-rose-900" }
    : isImminent
      ? { bg: "bg-amber-50", border: "border-amber-400", icon: "text-amber-700", text: "text-amber-900" }
      : { bg: "bg-blue-50", border: "border-blue-400", icon: "text-blue-700", text: "text-blue-900" };

  const headline = isLive
    ? "Maintenance en cours"
    : `Maintenance programmée ${countdownLabel(data.startsAt)}`;
  const startsLabel = formatFrDate(data.startsAt);
  const endsLabel = formatFrDate(data.endsAt);

  const onHide = () => {
    const key = `bypassgo:sched-maint-hide:${data.startsAt}`;
    sessionStorage.setItem(key, "1");
    setHidden(true);
  };

  return (
    <div
      className={`${tone.bg} ${tone.text} border-b-2 ${tone.border}`}
      data-testid="scheduled-maintenance-banner"
    >
      <div className="container mx-auto px-4 md:px-8 py-2.5 flex items-start md:items-center gap-3">
        <CalendarClock size={20} className={`${tone.icon} mt-0.5 md:mt-0 shrink-0`} />
        <div className="flex-1 text-xs md:text-sm leading-relaxed">
          <strong className="font-bold">{headline}</strong>
          <span className="hidden md:inline"> — </span>
          <span className="block md:inline">
            {isLive
              ? <>fin estimée : <strong>{endsLabel}</strong></>
              : <>début : <strong>{startsLabel}</strong> · fin : <strong>{endsLabel}</strong></>
            }
          </span>
          {data.note && (
            <span className="block md:inline md:ml-2 opacity-80 italic">
              « {data.note} »
            </span>
          )}
          <span className="block text-[11px] md:text-xs opacity-75 mt-0.5">
            Les compteurs de toutes les files seront automatiquement suspendus pendant l'indisponibilité.
          </span>
        </div>
        <button
          type="button"
          onClick={onHide}
          aria-label="Masquer la bannière"
          className={`${tone.icon} hover:opacity-70 shrink-0`}
          data-testid="scheduled-maintenance-banner-close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
